import * as React from "react";
import { AspectRatio, Box, Image, SimpleGrid, Stack, Text, Wrap } from "@chakra-ui/react";
import InTremorLogo from "assets/images/music/intremor-logo.png";
import InTremorShow from "assets/images/music/dnalounge.png";
import siteConfig from "configs/site-config";
import { Page, PillLink, Section, SectionHeading } from "components/page";

const ddhrTracks = [
  "https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/dhasemusic/ddhr-subete&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true",
  "https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/dhasemusic/underpass-qq&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true",
  "https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/dhasemusic/violence-feat-jose?si=2a9fd5a146f4425ab0a65562ae21906e&utm_source=clipboard&utm_medium=text&utm_campaign=social_sharing&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true",
];

const soloTracks = [
  "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/260759491&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true",
  "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/335493721&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true",
  "https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/dhasemusic/worst-season-ever-intentiontremor-dhase-remix&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true",
  "https://w.soundcloud.com/player/?url=https%3A//soundcloud.com/dhasemusic/walking-to-tjmaxx?si=2a9fd5a146f4425ab0a65562ae21906e&utm_source=clipboard&utm_medium=text&utm_campaign=social_sharing&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true",
];

const sourgreenTracks = [
  "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/playlists/303441347&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true",
  "https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/426441342&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true",
];

interface TracksProps {
  project: string;
  tracks: string[];
}

// Two-up grid of SoundCloud players; an odd last player spans the full row.
const Tracks = ({ project, tracks }: TracksProps) => (
  <SimpleGrid
    columns={{ base: 1, sm: 2 }}
    spacing={4}
    mt={6}
    sx={{ "& > :last-child:nth-of-type(odd)": { gridColumn: "1 / -1" } }}
  >
    {tracks.map((src, index) => (
      <Box key={src} borderRadius="lg" overflow="hidden" bg="line">
        <iframe
          title={`${project} on SoundCloud (${index + 1})`}
          width="100%"
          height="300"
          scrolling="no"
          allow="autoplay"
          src={src}
          style={{ display: "block", border: 0 }}
        />
      </Box>
    ))}
  </SimpleGrid>
);

const Music = () => {
  return (
    <Page title="Music">
      <Section label="Collaboration">
        <SectionHeading>DDHR</SectionHeading>
        <Text>
          A collaborative project with my friend Diego where we explore different sounds and ideas.
        </Text>
        <Tracks project="DDHR" tracks={ddhrTracks} />
      </Section>

      <Section label="Solo">
        <SectionHeading>Solo project</SectionHeading>
        <Text>I occasionally produce and release my own tracks or experiments.</Text>
        <Tracks project="Solo project" tracks={soloTracks} />
      </Section>

      <Section label="Band">
        <Image
          src={InTremorLogo}
          alt="Intention Tremor"
          w={{ base: "260px", md: "320px" }}
          mt={1}
          mb={6}
          _dark={{ filter: "invert(1)" }}
        />
        <Stack spacing={6}>
          <Text>
            When I was in San Francisco, I played bass guitar with a group of talented musicians
            which became a major creative outlet for me. On top of writing and performing music, I
            also contributed to the visual aspect of our art, designing the live light shows for our
            performances. We released an EP in 2019 and performed extensively around the San
            Francisco Bay Area. Our sound blends each member's diverse influences—best experienced
            firsthand. Find us on any streaming platform.
          </Text>
          <Wrap spacing={3}>
            {siteConfig.music.intremor.links.map((link) => (
              <PillLink key={link.url} to={link.url} external icon={link.icon}>
                {link.label}
              </PillLink>
            ))}
          </Wrap>
          <Box as="figure">
            <Image src={InTremorShow} borderRadius="lg" alt="Intention Tremor live at DNA Lounge" />
            <Text as="figcaption" mt={3} fontFamily="mono" fontSize="xs" color="muted">
              Live at DNA Lounge, San Francisco
            </Text>
          </Box>
          <Text>I also created a music video for our song Send it My Way! Check it out below.</Text>
          <AspectRatio ratio={16 / 9} borderRadius="lg" overflow="hidden" bg="line">
            <iframe
              src="https://www.youtube.com/embed/ac9rkWkWWxo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Embedded youtube: 'Send it my way' by intention tremor"
              style={{ border: 0 }}
            />
          </AspectRatio>
        </Stack>
      </Section>

      <Section label="Band">
        <SectionHeading>Sourgreen</SectionHeading>
        <Text>
          During my San Francisco years, I also played bass with Sourgreen. We recorded several
          tracks and gigged around the Bay Area. Our show at the Stork Club in Oakland was a
          particular highlight—check it out below.
        </Text>
        <Tracks project="Sourgreen" tracks={sourgreenTracks} />
      </Section>
    </Page>
  );
};

export default Music;
